import { createContext, ReactNode, useMemo, useState } from "react";
import { useGetMeQuery, User, UserRoleEnum } from "./generated/graphql";
import { getGraphQLClient } from "./authConfig";
import { ApolloClient, ApolloProvider, NormalizedCacheObject } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { setAuthenticatedUser } from "./telemetry";

export const AlertContext = createContext<[React.Dispatch<React.SetStateAction<boolean>>, React.Dispatch<React.SetStateAction<string>>]>([() => false, () => ""]);
export const ReflectDataContext = createContext<[boolean, React.Dispatch<React.SetStateAction<boolean>>]>([false, () => false]);
export const CurrentUserContext = createContext<User | null>(null);
export const TokenContext = createContext<string>("");

/** When set to a role, all useCurrentUser consumers will see that role instead of the real one. */
export const RoleOverrideContext = createContext<[UserRoleEnum | null, React.Dispatch<React.SetStateAction<UserRoleEnum | null>>]>([null, () => null]);

const ProviderRoot = (props: { children?: ReactNode | undefined; }) => {
    const { children } = props;

    const [reflectData, setreflectData] = useState(false);
    const [openAlert, setopenAlert] = useState(false);
    const [alertMessage, setalertMessage] = useState("");
    const [roleOverride, setRoleOverride] = useState<UserRoleEnum | null>(null);

    const { data: meData } = useGetMeQuery();
    const currentUser = (meData?.Me as User) ?? null;

    return (
        <CurrentUserContext.Provider value={currentUser}>
            <RoleOverrideContext.Provider value={[roleOverride, setRoleOverride]}>
                <ReflectDataContext.Provider value={[reflectData, setreflectData]}>
                    <AlertContext.Provider value={[setopenAlert, setalertMessage]}>
                        {children}
                    </AlertContext.Provider>
                </ReflectDataContext.Provider>
            </RoleOverrideContext.Provider>
        </CurrentUserContext.Provider>
    );
};

type IAppProviderProps = {
    children?: ReactNode | undefined;
};

const AppProvider = (props: IAppProviderProps) => {
    const { children } = props;
    const [client, setclient] = useState<null | ApolloClient<NormalizedCacheObject>>(null);
    const [token, settoken] = useState("");
    const auth0 = useAuth0();

    useMemo(() => {
        if (!auth0.isLoading && !auth0.isAuthenticated) {
            auth0.loginWithRedirect({
                appState: {
                    returnTo: window.location.pathname,
                },
            });
        }
        if (!auth0.isLoading && auth0.isAuthenticated) {
            auth0.getAccessTokenSilently()
                .then(token => {
                    settoken(token);
                    setclient(getGraphQLClient(token));
                    // Tag all App Insights telemetry with the logged-in user
                    if (auth0.user?.email) {
                        setAuthenticatedUser(auth0.user.email);
                    }
                }).catch(function(rej) {
                    auth0.loginWithRedirect({
                        appState: {
                            returnTo: window.location.pathname,
                        },
                    });
                  });
        }
    }, [auth0, settoken, setclient]);
    return (
        <>
            {client ?
                <ApolloProvider client={client}>
                    <TokenContext.Provider value={token}>
                        <ProviderRoot>
                            {children}
                        </ProviderRoot>
                    </TokenContext.Provider>
                </ApolloProvider> :
                <></>
            }
        </>
    );
};
export default AppProvider;